import { apiClient } from './client'

/**
 * `GET /api/v1/search[/suggestions]` (docs/api-spec.md §25, FR-M23.3, ADR-0058) — the backend fan-
 * out aggregator behind `lib/globalSearch.ts`. This client is a thin HTTP wrapper only; every rule
 * (grouping, caps, disclosure, access-scoping) lives server-side in `GlobalSearchService`.
 */

export type SearchScanScope = 'all' | 'recent'

/** One matched row, as the backend emits it — `secondary` may embed a RAW enum wire value (never
 * a translated string; i18n stays frontend-only, ADR-0058 §4.1). `parentId` is non-null only for a
 * `task` hit linked to a Case (the route-building field `lib/globalSearch.ts` needs). */
export interface SearchHitResponse {
  kind: string
  id: string
  label: string
  secondary: string | null
  parentId: string | null
}

export interface SearchGroupResponse {
  kind: string
  groupLabel: string
  items: SearchHitResponse[]
  hasMore: boolean
  scanScope: SearchScanScope
  error: string | null
}

function toQuery(q: string, types?: readonly string[]): string {
  const params = new URLSearchParams()
  params.set('q', q)
  for (const t of types ?? []) params.append('types', t)
  return params.toString()
}

export async function search(
  q: string,
  types?: readonly string[],
  signal?: AbortSignal,
): Promise<SearchGroupResponse[]> {
  const { data } = await apiClient.get<SearchGroupResponse[]>(`/search?${toQuery(q, types)}`, { signal })
  return data
}

/** Typeahead — top-N mixed hits across every kind, no per-group envelope, never audited (fires
 * per keystroke; ADR-0058 §4.1/§6 Q1). */
export async function searchSuggestions(q: string, signal?: AbortSignal): Promise<SearchHitResponse[]> {
  const { data } = await apiClient.get<SearchHitResponse[]>(
    `/search/suggestions?q=${encodeURIComponent(q)}`,
    { signal },
  )
  return data
}
